/**
 * Worklist side of the study warm-up (see lib/cornerstone/studyWarmup).
 *
 * `warm` on card open, `cancel` on collapse, `handoff` when the reader clicks
 * through to the viewer — that last one is what keeps an unmounting worklist
 * from cancelling the very loads the viewer is about to want.
 *
 * Multi-monitor changes where the bytes need to be: the memory that matters
 * is the viewer window's, not this one's. When a live viewer window answers
 * the role ping, the study is sent over the channel for THAT window to warm
 * (useWarmStudyChannel); otherwise the warm-up runs here, which still primes
 * the CDN edge for the window about to open.
 *
 * The Cornerstone chunk is reached through a dynamic import, exactly like
 * warmViewerEngine: a static import would pull it into the worklist bundle and
 * defeat the viewer's lazy loading.
 */
import { studySummaryPrefetchOptions } from '@/hooks/use-studies';
import { useFeatureFlags } from '@/lib/feature-flags/useFeatureFlag';
import { multiScreenService, windowChannel } from '@/lib/multiScreen';
import { discoverWindow } from '@/lib/multiScreen/windowRoles';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef } from 'react';
/** A card opened and closed within this window was a mis-click, not an intent. */
const WARM_DEBOUNCE_MS = 250;
/**
 * Server-side kill switch (`viewer.worklist_warmup_enabled`, surfaced by
 * GET /user/feature-flags). Read as "on unless explicitly off" so the first
 * card opened after login, while flags are still loading, is not left cold.
 */
const WORKLIST_WARMUP_FLAG = 'worklist_warmup';
export function useStudyWarmup(options = {}) {
    const debounceMs = options.debounceMs ?? WARM_DEBOUNCE_MS;
    const queryClient = useQueryClient();
    const { flags } = useFeatureFlags();
    const enabled = flags[WORKLIST_WARMUP_FLAG] !== false;
    // Read through a ref so `warm` keeps a stable identity across flag loads
    // (a TC card's effect depends on it; a new identity would restart the warm-up).
    const enabledRef = useRef(enabled);
    useEffect(() => {
        enabledRef.current = enabled;
    }, [enabled]);
    const timerRef = useRef(null);
    const pendingRef = useRef(null);
    const activeRef = useRef(null);
    const handedOffRef = useRef(null);
    const clearPending = () => {
        pendingRef.current = null;
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    };
    const cancel = useCallback((studyId) => {
        const pending = pendingRef.current;
        if (pending && (!studyId || pending.studyId === studyId))
            clearPending();
        const active = activeRef.current;
        if (active && (!studyId || active.studyId === studyId)) {
            active.cancelled = true;
            active.handle?.cancel();
            activeRef.current = null;
        }
    }, []);
    const start = useCallback(async (studyId, caseId) => {
        const entry = { studyId, cancelled: false, handle: null };
        activeRef.current = entry;
        try {
            if (multiScreenService.isViewerMultiScreenEnabled() && (await discoverWindow('viewer'))) {
                if (entry.cancelled)
                    return;
                windowChannel.send('WARM_STUDY', { studyId, caseId: caseId ?? undefined });
                return;
            }
            const summary = await queryClient.fetchQuery(studySummaryPrefetchOptions(studyId, caseId));
            if (entry.cancelled)
                return;
            const { warmStudy } = await import('@/lib/cornerstone');
            if (entry.cancelled)
                return;
            entry.handle = warmStudy({ summary, trigger: 'card', caseId });
        }
        catch {
            // Best effort: the viewer loads the study the way it always did.
        }
    }, [queryClient]);
    const warm = useCallback((studyId, caseId) => {
        if (!enabledRef.current)
            return;
        const active = activeRef.current;
        if (active && active.studyId === studyId && !active.cancelled)
            return;
        if (pendingRef.current?.studyId === studyId)
            return;
        cancel();
        pendingRef.current = { studyId, caseId: caseId ?? null };
        timerRef.current = setTimeout(() => {
            timerRef.current = null;
            const pending = pendingRef.current;
            pendingRef.current = null;
            if (pending)
                void start(pending.studyId, pending.caseId);
        }, debounceMs);
    }, [cancel, debounceMs, start]);
    const handoff = useCallback((studyId) => {
        handedOffRef.current = studyId;
        // A warm-up still waiting out its debounce is dropped rather than started:
        // the viewer is about to fetch this study itself.
        if (pendingRef.current?.studyId === studyId)
            clearPending();
    }, []);
    useEffect(() => () => {
        clearPending();
        const active = activeRef.current;
        if (active && active.studyId !== handedOffRef.current) {
            active.cancelled = true;
            active.handle?.cancel();
        }
        activeRef.current = null;
    }, []);
    return useMemo(() => ({ warm, cancel, handoff }), [warm, cancel, handoff]);
}
